import { format } from 'date-fns'
import { ReactNode } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/shared/ui/dialog'

type ReceiptItem = {
    name: string
    quantity: number
    price: number
}

type ReceiptVatTotal = {
    rate: number
    vatAmount: number
}

interface ReceiptPrintDialogProps {
    title: string
    description: string
    documentLabel: string
    items: ReceiptItem[]
    vatTotals: ReceiptVatTotal[]
    baseAmount: number
    total: number
    customerLine?: string
    onClose: () => void
    actions: (handlePrint: () => void) => ReactNode
}

export function ReceiptPrintDialog(props: ReceiptPrintDialogProps) {
    const { title, description, documentLabel, items, vatTotals, baseAmount, total, customerLine, onClose, actions } = props

    const handlePrint = () => {
        window.print()
    }

    return (
        <>
            <style>
                {`
                    @font-face {
                        font-family: 'RobotoMono';
                        src: url('/fonts/RobotoMono-Regular.ttf') format('truetype');
                    }

                    @page {
                        margin: 0;
                        padding: 0;
                        size: 72mm auto;
                    }

                    @media print {
                        html, body {
                            width: 72mm;
                            margin: 0 !important;
                            padding: 0 !important;
                        }

                        body * {
                            visibility: hidden;
                        }

                        #receipt-print-content, #receipt-print-content * {
                            visibility: visible;
                        }

                        #receipt-print-content {
                            position: relative;
                            left: 0;
                            top: -50mm;
                            width: 66mm;
                            max-width: 66mm;
                            margin: 0;
                            padding: 0;
                            font-family: 'RobotoMono', monospace;
                            font-size: 12px;
                            line-height: 1.3;
                            background: white;
                            box-sizing: border-box;
                            white-space: pre-wrap;
                        }

                        #receipt-print-content * {
                            box-sizing: border-box;
                        }

                        .no-print {
                            display: none !important;
                        }

                        * {
                            page-break-after: avoid;
                            page-break-before: avoid;
                            page-break-inside: avoid;
                        }
                    }
                `}
            </style>
            <Dialog open={true} onOpenChange={onClose}>
                <DialogContent className='max-h-[90vh] max-w-[600px] p-0'>
                    <div className='no-print border-b p-4'>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{description}</DialogDescription>
                    </div>

                    <div className='flex max-h-[calc(90vh-12rem)] items-center justify-center bg-gray-50 p-4'>
                        <div
                            id='receipt-print-content'
                            className='mx-auto w-[450px] overflow-y-auto bg-white p-8 text-[14px] leading-[1.4] shadow-sm print:w-[76mm] print:pl-[1mm] print:pr-[1mm] print:pb-[8mm] print:pt-[2mm] print:text-[12px] print:leading-[1.3]'
                        >
                            <div className='text-center print:mt-0 print:pt-0'>
                                <div className='print:mt-0'>* * * * *</div>
                                <div className='font-bold'>DONDERSTEEN RESORT</div>
                                <div className='font-bold'>DONDERSTEEN S.L.</div>
                                <div>B22748586</div>
                                <div>Camí de Can Solé Marí, 6</div>
                                <div>08480 L'Ametlla del Vallès</div>
                                <div className='mb-4'>Barcelona, España</div>
                            </div>

                            <div className='mb-4'>
                                <div className='flex justify-between'>
                                    <div>{documentLabel}</div>
                                    <div>{format(new Date(), 'dd/MM/yy, HH:mm:ss')}</div>
                                </div>
                                {customerLine && <div className='mt-1 text-xs'>{customerLine}</div>}
                            </div>

                            <div className='mb-1 grid grid-cols-[20px_1fr_70px] gap-x-2'>
                                <div>#</div>
                                <div>Producto</div>
                                <div className='text-right'>Precio</div>
                            </div>

                            <div className='mb-4'>
                                {items.map((item, index) => (
                                    <div key={index} className='grid grid-cols-[20px_1fr_70px] gap-x-2'>
                                        <div>{item.quantity}</div>
                                        <div className='break-words whitespace-pre-line'>{item.name}</div>
                                        <div className='text-right'>{item.price.toFixed(2)}€</div>
                                    </div>
                                ))}
                            </div>

                            <div className='mb-4'>
                                <div className='grid grid-cols-[20px_1fr_70px] gap-x-2'>
                                    <div></div>
                                    <div>Base Imponible</div>
                                    <div className='text-right'>{baseAmount.toFixed(2)}€</div>
                                </div>

                                {vatTotals.map(vat => (
                                    <div key={vat.rate} className='grid grid-cols-[20px_1fr_70px] gap-x-2'>
                                        <div></div>
                                        <div>IVA {vat.rate}%</div>
                                        <div className='text-right'>{vat.vatAmount.toFixed(2)}€</div>
                                    </div>
                                ))}
                            </div>

                            <div className='mb-4 text-center text-xl font-bold print:text-[14px]'>
                                Total: {total.toFixed(2)}€
                            </div>
                        </div>
                    </div>

                    <div className='no-print flex justify-end gap-4 border-t bg-white p-4'>{actions(handlePrint)}</div>
                </DialogContent>
            </Dialog>
        </>
    )
}
